import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  fsGetWater,
  fsLogWater,
  fsSetWaterGoal,
  fsGetWeekWater,
} from '../services/firestoreDb';
import { localDateKey, localDateKeys } from '../utils/dateKey';

const ML_PER_GLASS = 250;

function storageScope(uid) {
  return uid ? `user_${uid}` : 'guest';
}

function getToday() {
  return localDateKey();
}

function localGetAmountMl(uid = null) {
  const today = getToday();
  const rawCurrent = localStorage.getItem(`sm_water_ml_${storageScope(uid)}_${today}`) ??
    (!uid ? localStorage.getItem(`sm_water_ml_${today}`) : null);
  const current = Number(rawCurrent);
  if (rawCurrent !== null && Number.isFinite(current) && current >= 0) return Math.round(current);
  const legacy = !uid ? localStorage.getItem(`sm_water_${today}`) : null;
  return Math.round(parseFloat(legacy || '0') * ML_PER_GLASS);
}

function localSetAmountMl(n, uid = null) {
  localStorage.setItem(
    `sm_water_ml_${storageScope(uid)}_${getToday()}`,
    String(Math.max(0, Math.round(n)))
  );
}

function localGetGoalMl(uid = null) {
  const rawCurrent = localStorage.getItem(`sm_water_goal_ml_${storageScope(uid)}`) ??
    (!uid ? localStorage.getItem('sm_water_goal_ml') : null);
  const current = Number(rawCurrent);
  if (rawCurrent !== null && Number.isFinite(current) && current >= ML_PER_GLASS && current <= 20000) {
    return Math.round(current);
  }
  const legacy = !uid ? Number.parseInt(localStorage.getItem('sm_water_goal') || '8', 10) : 8;
  return Number.isFinite(legacy) && legacy >= 1 && legacy <= 80
    ? legacy * ML_PER_GLASS
    : 8 * ML_PER_GLASS;
}

function localSetGoalMl(g, uid = null) {
  localStorage.setItem(
    `sm_water_goal_ml_${storageScope(uid)}`,
    String(Math.min(20000, Math.max(ML_PER_GLASS, Math.round(g))))
  );
}

function localGetWeekData(uid = null) {
  return localDateKeys(7).map((date) => {
    const day = new Date(`${date}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short' });
    const rawCurrent = localStorage.getItem(`sm_water_ml_${storageScope(uid)}_${date}`) ??
      (!uid ? localStorage.getItem(`sm_water_ml_${date}`) : null);
    const current = Number(rawCurrent);
    const legacy = !uid ? localStorage.getItem(`sm_water_${date}`) : null;
    const amountMl = rawCurrent !== null && Number.isFinite(current)
      ? Math.max(0, Math.round(current))
      : Math.round(parseFloat(legacy || '0') * ML_PER_GLASS);
    return { date, day, amountMl, glasses: amountMl / ML_PER_GLASS };
  });
}

export function useWater() {
  const { user } = useAuth();
  const uid = user?.id;

  const [amountMl, setAmountMlState] = useState(() => localGetAmountMl(null));
  const [goalMl, setGoalMlState] = useState(() => localGetGoalMl(null));
  const [weekData, setWeekData] = useState(() => localGetWeekData(null));
  const amountRef = useRef(amountMl);
  const writeQueue = useRef(Promise.resolve());

  useEffect(() => {
    let active = true;
    const cachedAmount = localGetAmountMl(uid);
    const cachedGoal = localGetGoalMl(uid);
    amountRef.current = cachedAmount;
    setAmountMlState(cachedAmount);
    setGoalMlState(cachedGoal);
    setWeekData(localGetWeekData(uid));

    if (!uid) return () => { active = false; };
    Promise.all([fsGetWater(uid), fsGetWeekWater(uid)])
      .then(async ([today, week]) => {
        if (!active) return;
        const shouldMigrateGuest = today === null || today.hasDaily === false;
        const guestAmount = localGetAmountMl(null);
        const nextAmount = shouldMigrateGuest
          ? (cachedAmount > 0 ? cachedAmount : guestAmount)
          : today.amountMl;
        localSetAmountMl(nextAmount, uid);
        amountRef.current = nextAmount;
        setAmountMlState(nextAmount);

        if (shouldMigrateGuest && nextAmount > 0) {
          await fsLogWater(uid, nextAmount).catch(() => {});
          setWeekData(localGetWeekData(uid));
        } else if (week !== null) {
          setWeekData(week);
        }

        const nextGoal = today?.hasGoal
          ? today.goalMl
          : (cachedGoal !== 8 * ML_PER_GLASS ? cachedGoal : localGetGoalMl(null));
        localSetGoalMl(nextGoal, uid);
        setGoalMlState(nextGoal);
        if (!today?.hasGoal) await fsSetWaterGoal(uid, nextGoal).catch(() => {});
      })
      .catch(() => {});
    return () => { active = false; };
  }, [uid]);

  const persistAmount = useCallback((next) => {
    if (!uid) return;
    writeQueue.current = writeQueue.current
      .catch(() => {})
      .then(() => fsLogWater(uid, next));
  }, [uid]);

  const addGlass = useCallback(
    (glasses) => {
      const next = Math.max(0, Math.round(amountRef.current + glasses * ML_PER_GLASS));
      amountRef.current = next;
      localSetAmountMl(next, uid);
      setAmountMlState(next);
      setWeekData(localGetWeekData(uid));
      persistAmount(next);
    },
    [uid, persistAmount]
  );

  const setGlasses = useCallback(
    (n) => {
      const next = Math.max(0, Math.round(parseFloat(n) * ML_PER_GLASS));
      if (!Number.isFinite(next) || next > 100000) return;
      amountRef.current = next;
      localSetAmountMl(next, uid);
      setAmountMlState(next);
      setWeekData(localGetWeekData(uid));
      persistAmount(next);
    },
    [uid, persistAmount]
  );

  const setGoal = useCallback(
    (n) => {
      const glasses = parseInt(n, 10);
      if (!Number.isFinite(glasses) || glasses < 1 || glasses > 80) return;
      const next = glasses * ML_PER_GLASS;
      localSetGoalMl(next, uid);
      setGoalMlState(next);
      if (uid) fsSetWaterGoal(uid, next).catch(() => {});
    },
    [uid]
  );

  return {
    amountMl,
    goalMl,
    glasses: amountMl / ML_PER_GLASS,
    goal: goalMl / ML_PER_GLASS,
    weekData,
    addGlass,
    setGlasses,
    setGoal,
  };
}
